import React, { useCallback, useContext, useEffect } from 'react';
import { DateRange, DayPicker } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { ToggleFormElementContext } from 'component/Search/Form/__context/toggleFormElementProvider';
import { ChangedHighlightType } from 'component/Search/Form/types';
import Input from 'component/Search/Input';
import { CustomSearchInputClassList } from 'component/Search/Input/types';

import classNames from 'classnames';
import { addDays } from 'date-fns';
import 'react-day-picker/dist/style.css';
import { de } from 'date-fns/locale';
import { useAtomValue, useSetAtom } from 'jotai';

import { useOutsideClick } from 'hooks/useOutsideClick';
import { QaIdentifier, useQa } from 'hooks/useQa';

import { refreshNoteVisibility } from 'data/Atom/Component';
import {
    changedOpenTypeAtom,
    changedTooltipAtom,
} from 'data/Atom/IntegratedOvernight';

import { ReactComponent as IconCross } from 'media/svg/cross.svg';

import { getDate } from 'utility/Format/Date';

import { ValidationType } from 'types/Search/form';

import { config } from '../__config';
import {
    ActiveDatepicker,
    DatePickerCallbackInterface,
    FormType,
} from '../types';

import { useDateValidation } from './__hooks/useDateValidation';

import styles from './Split.module.scss';

interface DatepickerProps {
    formType: FormType;
    dateRange?: DateRange;
    label: string[];
    placeholder: string[];
    customClassList?: CustomSearchInputClassList;
    callback: DatePickerCallbackInterface;
    showHighlightData?: boolean;
    activeMonth?: Date;
    onValidate?: (type: ValidationType, isEmpty: boolean) => void;
}

const Datepicker = ({
    label,
    placeholder,
    callback,
    customClassList,
    showHighlightData,
    dateRange,
    activeMonth,
    onValidate,
}: DatepickerProps) => {
    const today = new Date();
    const setShowTooltip = useSetAtom(changedTooltipAtom);
    const [departureLabel, returnLabel] = label;
    const [departurePlaceholder, returnPlaceholder] = placeholder;
    const openType = useAtomValue(changedOpenTypeAtom);
    const { t } = useTranslation();
    const { activeDatepicker, setActiveDatepicker } = useContext(
        ToggleFormElementContext
    );
    const qaAttr = useQa(QaIdentifier.Search_Form_Datepicker_Layer);
    const setRefreshNote = useSetAtom(refreshNoteVisibility);

    const { validateFromStartDate, validateFromEndDate, range } =
        useDateValidation({
            currentValues: {
                from: dateRange?.from ?? addDays(today, 1),
                to: dateRange?.to ?? addDays(today, config.defaultDaysBetween),
            },
            minStart: addDays(today, 1),
            minRange: config.minDaysBetween,
            maxRange: config.maxRange,
            callbacks: {
                triggerStartDateSelection: () => {
                    setActiveDatepicker(ActiveDatepicker.Departure);
                    setRefreshNote(true);
                    setShowTooltip(false);
                },
                triggerEndDateSelection: () => {
                    setActiveDatepicker(ActiveDatepicker.Return);
                    setRefreshNote(true);
                    setShowTooltip(false);
                },
                triggerCloseDateSelection: (newDateSelection) => {
                    handleClose(newDateSelection);
                },
                onValueChange: callback.onValueChange,
            },
        });

    const handleInputClick = useCallback(
        (type: ActiveDatepicker) => {
            setActiveDatepicker(type);
        },
        [setActiveDatepicker]
    );

    const handleClose = useCallback(
        (newDateSelection = range) => {
            if (activeDatepicker !== ActiveDatepicker.None) {
                callback.onClose?.();
                setTimeout(
                    () => setActiveDatepicker(ActiveDatepicker.None),
                    config.delayForEndElementCloseAfterRangeComplete
                );
            }
            onValidate?.(
                ValidationType.TravelDates,
                !newDateSelection?.from || !newDateSelection?.to
            );
        },
        [setActiveDatepicker, activeDatepicker, callback, onValidate, range]
    );

    const handleDateSelection = (selectedDay: Date) => {
        if (activeDatepicker === ActiveDatepicker.Departure) {
            validateFromStartDate(selectedDay);
        }
        if (activeDatepicker === ActiveDatepicker.Return) {
            validateFromEndDate(selectedDay);
        }
    };

    const getFormattedDate = (date: Date) =>
        getDate(date, config.format, config.dateTimeFormatOptions);

    useEffect(() => {
        setActiveDatepicker(
            openType === ChangedHighlightType.TravelDate
                ? ActiveDatepicker.Departure
                : ActiveDatepicker.None
        );
    }, [openType, setActiveDatepicker]);

    const datepickerRef = useOutsideClick<HTMLDivElement>(
        () => activeDatepicker !== ActiveDatepicker.None && handleClose()
    );

    return (
        <div ref={datepickerRef}>
            <div className={styles.DayPickerWrapper}>
                <>
                    <Input
                        labelText={departureLabel}
                        placeholder={departurePlaceholder}
                        value={getFormattedDate(range.from)}
                        callbacks={{
                            onClick: () => {
                                handleInputClick(ActiveDatepicker.Departure);
                                callback.onOpen(range.from);
                            },
                        }}
                        customClassList={{
                            InputWrapper: customClassList?.InputWrapper,
                            Label: customClassList?.Label,
                            Input: classNames(
                                styles.Input,
                                {
                                    [styles.Highlight]: showHighlightData,
                                },
                                customClassList?.Input
                            ),
                        }}
                        qaId={
                            QaIdentifier.Search_Form_Datepicker_Range_Departure_Value
                        }
                    />
                    <Input
                        labelText={returnLabel}
                        placeholder={returnPlaceholder}
                        value={getFormattedDate(range.to)}
                        callbacks={{
                            onClick: () => {
                                handleInputClick(ActiveDatepicker.Return);
                                callback.onOpen(range.to);
                            },
                        }}
                        customClassList={{
                            InputWrapper: customClassList?.InputWrapper,
                            Label: customClassList?.Label,
                            Input: classNames(
                                styles.Input,
                                {
                                    [styles.Highlight]: showHighlightData,
                                },
                                customClassList?.Input
                            ),
                        }}
                        qaId={
                            QaIdentifier.Search_Form_Datepicker_Range_Return_Value
                        }
                    />
                </>
            </div>
            {activeDatepicker !== ActiveDatepicker.None && (
                <div
                    className={classNames(styles.Overlay, {
                        [styles.Return]:
                            activeDatepicker === ActiveDatepicker.Return,
                    })}
                    {...qaAttr}
                >
                    <h3 className={styles.Header}>
                        {activeDatepicker === ActiveDatepicker.Departure &&
                            t('TRANS-when-is-your-earliest-departure')}
                        {activeDatepicker === ActiveDatepicker.Return &&
                            t('TRANS-when-is-your-latest-return')}
                    </h3>

                    <span
                        className={styles.Close}
                        onClick={() => handleClose()}
                    >
                        <IconCross />
                    </span>
                    {showHighlightData && (
                        <div
                            className={styles.HighlightData}
                            dangerouslySetInnerHTML={{
                                __html: t(
                                    'TRANS-travel-date-changed-review-the-choice'
                                ),
                            }}
                        />
                    )}
                    <DayPicker
                        locale={de} // TODO: find ways to automate language settings - {de} from date-fns is type object, not string
                        numberOfMonths={config.numberofMonths}
                        selected={range}
                        onSelect={(_, selectedDay) => {
                            handleDateSelection(selectedDay);
                        }}
                        mode={'range'}
                        ISOWeek
                        min={config.minDaysBetween}
                        disabled={{
                            before: addDays(today, 1),
                            after: addDays(today, config.maxFutureDay),
                        }}
                        month={activeMonth}
                        onMonthChange={callback.onMonthChange}
                        modifiers={{
                            start: range.from ?? false,
                            end: range.to ?? false,
                            holidays: config.holidays,
                            vacations: config.vacations,
                            weekend: (date: Date) => {
                                const day = date.getDay();
                                return day === 6 || day === 0; // 6 = Saturday, 0 = Sunday
                            },
                        }}
                        modifiersClassNames={{
                            today: styles.Today,
                            range_start: styles.RangeStart,
                            range_end: styles.RangeEnd,
                            vacations: styles.Vacation,
                            range_middle: styles.SelectedRange,
                            range: styles.SelectedRange,
                            selected: styles.SelectedRange,
                            weekend: styles.Weekend,
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default Datepicker;
